/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-props-no-spreading */
import { CSSProperties, FC, Fragment, ReactNode, useCallback, useMemo } from 'react';

import {
  Cell,
  Row,
  useSortBy,
  UseSortByColumnProps,
  useTable,
  UseTableColumnProps,
  UseTableRowProps,
} from 'react-table';
import { cloneDeep, isBoolean, isNumber, isString } from 'lodash';
import classNames from 'classnames';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { getMaxLength } from 'common/util/array';
import { PQLinkedValue } from 'common/util/pqUtil';

import { DraggableRow } from 'components/common/Table/DraggableRow';

import './Table.scss';

// constants to help with auto-sizing columns
const AUTO_SIZE_SAMPLES = 100; // how many data values to look at for auto-sizing a column
const AVG_CHAR_WIDTH = 7.5; // average character width in pixels (measured in Chrome/Firefox/Safari)
const MIN_COL_WIDTH = 60; // default minimum column width in pixels (before padding)
const MAX_COL_WIDTH = 780; // default maximum column width in pixels (before padding)
const TABLE_PADDING_WIDTH = 20; // each cell has 10px left and right padding

type TableCellContent = string | ReactNode | PQLinkedValue;

export type TableHeader = {
  content?: TableCellContent;
  field: string;
  width?: number | string;
  className?: string;
  alignment?: K.ALIGNMENT;
  isNotSortable?: boolean;
  footer?: TableCellContent;
  autoSize?: boolean;
  minWidth?: number;
  maxWidth?: number;
  renderCell?: (cell: Cell<any>) => TableCellContent;
  sortMethod?: (a: any, b: any, columnId: any) => 1 | 0 | -1;
  isNumeric?: boolean;
};

export type TableOptions = {
  isStriped?: boolean;
  isSortable?: boolean;
  sidePadding?: number;
  hoverRow?: boolean;
  size?: K.SIZE;
  tableClass?: string;
  headerClass?: string;
  headerItemClass?: string;
  rowClass?: string;
  itemClass?: string;
  footerClass?: string;
  footerItemClass?: string;
  autoSizeColumns?: boolean;
  minColWidth?: number;
  maxColWidth?: number;
  initialState?: { [key: string]: any };
  noHeaders?: boolean;
  noFooters?: boolean;
  showBarsColumn?: string;
};

export type TableRow = {
  [key: string]: TableCellContent;
  isDraggableRow?: boolean;
};

type PropTypes = { [key: string]: any };

type TableProps = {
  headers: Array<TableHeader>;
  data: Array<TableRow>;
  options?: TableOptions;
  draggableOptions?: {
    isDraggable: boolean;
    getRowIdCallback: (row: TableRow) => string;
    rowMoveCallback: (data: Array<TableRow>) => void;
    rowDropCallback?: (currentRowId: number | string, position: number) => void;
  };
  userRowProp?: (userProps: Row<any>) => any;
  testId?: ELEMENT;
  testIdRow?: ELEMENT;
  testIdHeader?: ELEMENT;
  draggableRowTestId?: ELEMENT;
  testIdFooter?: ELEMENT;
  customRowIdCallback?: (row: TableRow) => string;
};

/**
 * Table is a generic component used to display tabular data. It is built on the react-table library. That library exposes hooks rather than
 * a UI, leaving the UI up to us. We currently use an HTML table as the implementation (we could use CSS grid or absolute positioning
 * if we chose, for example). The motivation behind this component is to expose a simpler API than that of react-table, and to provide an
 * implementation of the UI.
 *
 * The look of the table can be customized by providing classes to set on the various parts of the table.
 *
 * @prop headers    an array of header specification objects with the following properties:
 *
 *         content           content of the header cell (defaults to empty string); if component/JSX, provide function that returns it
 *         field             field used to access row values in the data
 *         width             width of the column
 *         className         additional class name to apply to cells in this column
 *         alignment         K.ALIGNMENT.START | K.ALIGNMENT.CENTER | K.ALIGNMENT.END
 *         isNotSortable     if true, the column cannot be sorted
 *         footer            custom column footer
 *         autoSize          if true, set the width of the column based on a sample of the data
 *         minWidth          minimum width for auto-sized columns (before padding)
 *         maxWidth          maximum width for auto-sized columns (before padding)
 *         renderCell        custom function for rendering cell content
 *         isNumeric         if true, the column has numeric data (which will be right-aligned)
 *
 * @prop data       an array of row data objects for the table; each bit of data can be a string or JSX
 *
 * @prop options    options that control the behavior and/or display of the table:
 *
 *         isStriped          true to have the content zebra-striped (alternating dark and light background for each row)
 *         isSorted           true to allow the user to sort by clicking a column header
 *         sidePadding        amount of padding on left and right sides, in pixels
 *         hoverRow           true to have rows highlight on hover (for non-striped tables only)
 *         size               large | medium (default) | small
 *         tableClass         optional class(es) to set on the outermost element
 *         headerClass        optional class(es) to set on the header row
 *         headerItemClass    optional class(es) to set on each cell in the header row
 *         rowClass           optional class(es) to set on each data row
 *         itemClass          optional class(es) to set on each cell in a data row
 *         footerClass        optional class(es) to set on the footer row
 *         footerItemClass    optional class(es) to set on each cell in a footer row
 *         autoSizeColumns    if true, columns default to auto-sizing
 *         minWidth           minimum width for auto-sized columns (before padding)
 *         maxWidth           maximum width for auto-sized columns (before padding)
 *         initialState       initial state to pass to react-table (see docs)
 *         noHeaders          if true, omit the header row
 *         showBarsColumn     if true, display bar ranges in a table column wrt to the last numeric column values
 *
 * @prop draggableOptions    options that control drag-and-drop behavior of the table;
 *
 *         isDraggable        true to have the drag-and-drop functionality, but need to provide getRowIdCallback, rowMoveCallback, rowDropCallback
 *         getRowIdCallback   required function for providing row's id for drag-and-drop; optional for other cases;
 *         rowMoveCallback    required function for updating data when we use drag-and-drop; optional for other cases;
 *         rowDropCallback    function for handling onDrop event for drag-and-drop;
 *
 * @prop userRowProp    optional function which gets custom props to the row
 * @prop testId         unique identifier for testing
 *
 * TODO: option for headerless table
 * TODO: do we need a way to style individual columns (both in header and body)?
 * TODO: figure out TS type for column
 */
const Table: FC<TableProps> = ({
  headers,
  data,
  options,
  draggableOptions,
  userRowProp,
  testId,
  testIdRow,
  testIdHeader,
  testIdFooter,
  draggableRowTestId,
  customRowIdCallback,
}: TableProps) => {
  const isSortable = !!options?.isSortable;
  const isStriped = !!options?.isStriped;
  const sidePadding = options?.sidePadding;
  const hoverRow = !!options?.hoverRow;
  const size = options?.size || K.SIZE.MEDIUM;
  const autoSizeColumns = !!options?.autoSizeColumns;
  const initialState = options?.initialState || {};
  const noHeaders = options?.noHeaders;
  const noFooters = options?.noFooters ?? true; // Default Tables to having no footer since we rarely use this
  const showBarsColumn = options?.showBarsColumn === 'true';
  // add user-provided and conditional classes to our base classes
  const headerClass = classNames('Table__HeaderRow', options?.headerClass);
  const headerItemClass = classNames(
    'Table__Item',
    'Table__HeaderItem',
    options?.headerItemClass,
    'Table__HeaderItem--' + size,
    { 'Table__HeaderItem--Sortable': isSortable },
  );
  const rowClass = classNames('Table__Row', options?.rowClass, { 'Table__Row--Striped': isStriped });
  const itemClass = classNames('Table__Item', options?.itemClass, 'Table__Item--' + size);
  const footerClass = classNames('Table__FooterRow', options?.footerClass);
  const footerItemClass = classNames(
    'Table__Item',
    'Table__FooterItem',
    options?.footerItemClass,
    'Table__HeaderItem--' + size,
  );

  const isDraggable = !!draggableOptions?.isDraggable;
  const defaultGetRowId =
    customRowIdCallback ||
    ((row: TableRow, relativeIndex: number, parent?: Row<TableRow>): string =>
      parent ? [parent.id, relativeIndex].join('.') : relativeIndex.toString());
  const getRowIdCallback = draggableOptions?.getRowIdCallback || defaultGetRowId;
  const rowMoveCallback = draggableOptions?.rowMoveCallback || K.EMPTY_FUNCTION;
  const rowDropCallback = draggableOptions?.rowDropCallback || K.EMPTY_FUNCTION;

  const tableHeaders = useMemo(() => cloneDeep(headers), [headers]);

  // convert user-provided header specifications into the form react-table uses for columns; must be memoized for react-table
  const columns = useMemo(
    () =>
      tableHeaders.map((header) => {
        let content = header.content;
        const columnOptions: any = {
          id: header.field,
          Header: content || '',
          accessor: (theData: any) => theData[header.field],
          Footer: header.footer || '',
          // Adding react-table "Cell" property only if it's provided
          ...(header.renderCell && { Cell: header.renderCell }),
          // Adding react-table "sortType" property only if it is provided *NOTE - a custom sort function must be memoized.
          ...(header.sortMethod && { sortType: header.sortMethod }),
        };
        if (isSortable && header.isNotSortable) {
          columnOptions.disableSortBy = true;
        }
        return columnOptions;
      }),
    [tableHeaders, isSortable],
  );
  const numColumns = columns.length;
  const getRowId = useCallback(getRowIdCallback, []);
  let maxValue = 0;
  if (showBarsColumn) {
    data.forEach((row) => {
      Object.values(row).forEach((val, idx) => {
        if (idx === Object.keys(row).length - 1) {
          const vals = val as PQLinkedValue;
          if (val && (val as PQLinkedValue)) {
            // get max value from list of values to set width of the bar
            maxValue = Math.max(maxValue, vals.value as number);
          }
        }
      });
    });
  }

  // memoize user-provided data as required by react-table
  const tableData = useMemo(() => data, [data]);

  // Invoke the useTable hook. It returns stuff we can use to set up our table. Add in the optional sorting hook.
  const {
    getTableProps, // function that returns properties to apply to enclosing table
    getTableBodyProps, // function that returns properties to apply to enclosing table body
    headerGroups, // array of info for header rows (we normally have just one)
    rows, // array of materialized row objects
    prepareRow, // function to materialize a row when we are ready to display it
    footerGroups,
  } = useTable(
    {
      columns,
      data: tableData,
      getRowId,
      initialState,
    },
    useSortBy,
  );

  // if we need to auto-size columns, sample the data and set width based on longest sampled value
  tableHeaders.forEach((header) => {
    if (!header.width && (autoSizeColumns || header.autoSize)) {
      const sampleValues: Array<string> = [];
      for (let i = 0; i < data.length && sampleValues.length < AUTO_SIZE_SAMPLES; i += 1) {
        const value = data[i][header.field];
        // only consider string/number/boolean; ignore JSX
        if (isString(value) && value) {
          sampleValues.push(value);
        } else if (isNumber(value) || isBoolean(value)) {
          sampleValues.push(String(value));
        }
      }
      // don't forget column header text
      if (isString(header.content)) {
        sampleValues.push(header.content);
      }

      const width = getMaxLength(sampleValues) * AVG_CHAR_WIDTH;
      const min = header.minWidth || options?.minColWidth || MIN_COL_WIDTH;
      const max = header.maxWidth || options?.maxColWidth || MAX_COL_WIDTH;
      header.width = Math.max(Math.min(width, max), min) + TABLE_PADDING_WIDTH;
    }
  });

  // If we have sized columns, use <colgroup> and <col> to set those up.
  let colSection = null;
  const numHeadersWithWidth = tableHeaders.filter((header) => isNumber(header.width)).length;
  if (numHeadersWithWidth > 0) {
    colSection = (
      <colgroup>
        {tableHeaders.map((header) => {
          let width = header.width || 'auto';
          let maxWidth = header.width || 'auto';
          if (isNumber(header.width)) {
            width = header.width + 'px';
            maxWidth = header.width + 'px';
          }
          const style = { width, maxWidth };
          return <col style={style} key={header.field} />;
        })}
      </colgroup>
    );
  }

  const tableTypeClass = numHeadersWithWidth === tableHeaders.length ? 'Table--Fixed' : 'Table--Full';
  const tableClass = classNames('Table', options?.tableClass, tableTypeClass, { 'Table--Hover': hoverRow });

  // If sorting is enabled, show up or down arrow to indicate the sort direction. Note that react-table toggles among three states:
  // original order, descending, ascending.
  const ascIcon = <KeyboardArrowUpIcon fontSize="small" />;
  const descIcon = <KeyboardArrowDownIcon fontSize="small" />;

  // add an up or down arrow if needed
  const getSortIndicator = (column: UseSortByColumnProps<UseTableColumnProps<{}>>): ReactNode => {
    if (!isSortable || !column || !column.isSorted) {
      return null;
    }
    return column.isSortedDesc ? descIcon : ascIcon;
  };

  const moveRow = (dragIndex: number, hoverIndex: number): void => {
    const records = tableData.map((row: TableRow) => ({ ...row }));
    const dragRecord = records[dragIndex];
    // remove dragged item
    records.splice(dragIndex, 1);
    // insert dragged item
    records.splice(hoverIndex, 0, dragRecord);
    rowMoveCallback(records);
  };

  // handle sidePadding (table property)
  const getColumnStyle = useCallback(
    (index: number, baseStyle?: CSSProperties) => {
      const style: CSSProperties = baseStyle ? cloneDeep(baseStyle) : {};
      if (sidePadding) {
        if (index === 0) {
          style.paddingLeft = sidePadding + 'px';
        } else if (index === numColumns - 1) {
          style.paddingRight = sidePadding + 'px';
        }
      }
      return style;
    },
    [numColumns, sidePadding],
  );

  // custom function for header props in case we need to add padding to the style
  const getHeaderProps = (column: any, index: number): PropTypes => {
    const props = column.getHeaderProps(isSortable ? column.getSortByToggleProps() : undefined);
    props.style = getColumnStyle(index, props.style);
    return props;
  };

  // returns a CSS class that aligns content left or right as appropriate
  const getAlignmentClass = (header: TableHeader, baseClass: string): string => {
    const alignment = header.alignment || (header.isNumeric && !showBarsColumn ? K.ALIGNMENT.END : K.ALIGNMENT.START);
    return classNames({
      [`${baseClass}--Left`]: alignment === K.ALIGNMENT.START,
      [`${baseClass}--Right`]: alignment === K.ALIGNMENT.END,
    });
  };

  // a single header cell
  const getColumn = (column: any, index: number): ReactNode => {
    const headerCellClass = classNames(
      headerItemClass,
      headers[index]?.className,
      getAlignmentClass(headers[index], 'Table__HeaderItem'),
    );
    return (
      <th className={headerCellClass} {...getHeaderProps(column, index)}>
        {column.render('Header')}
        {getSortIndicator(column)}
      </th>
    );
  };

  // a single footer cell
  const getFooter = (column: any, index: number): ReactNode => {
    const footerCellClass = classNames(
      footerItemClass,
      headers[index]?.className,
      getAlignmentClass(headers[index], 'Table__HeaderItem'),
    );
    return (
      <td className={footerCellClass} {...getHeaderProps(column, index)}>
        {column.render('Footer')}
      </td>
    );
  };

  // the header row
  const headerSection = noHeaders
    ? null
    : headerGroups.map((headerGroup) => (
        <tr className={headerClass} {...headerGroup.getHeaderGroupProps()} data-test-id={testIdHeader}>
          {headerGroup.headers.map((column, index) => getColumn(column, index))}
        </tr>
      ));

  const footerSection = noFooters
    ? null
    : footerGroups.map((footerGroup) => (
        <tr className={footerClass} {...footerGroup.getFooterGroupProps()} data-test-id={testIdFooter}>
          {footerGroup.headers.map((column, index) => getFooter(column, index))}
        </tr>
      ));

  // a data row
  const getCells = (row: UseTableRowProps<{}>): ReactNode =>
    row.cells.map((cell, index) => {
      const cellClass = classNames(
        itemClass,
        headers[index].className,
        getAlignmentClass(headers[index], 'Table__Item'),
      );
      let width = '0%';
      if (showBarsColumn && index === headers.length - 1 && maxValue) {
        if (cell.value.value >= maxValue) {
          width = '100%'; // maxWidth
        } else if (cell.value.value < maxValue) {
          const percentage = (cell.value.value / maxValue) * 100;
          width = `${String(percentage)}%`;
        }
      }

      return (
        <Fragment key={index}>
          {showBarsColumn && index === headers.length - 1 ? (
            <td className={cellClass} style={getColumnStyle(index)} {...cell.getCellProps()}>
              <div className="Table__BarColumn">
                <div className="Table__BarColumn__Value">{cell.render('Cell')}</div>
                <div className="Table__BarColumn__Block">
                  <div className="Table__BarColumn__BarRange" style={{ width: width }}></div>
                </div>
              </div>
            </td>
          ) : (
            <td className={cellClass} style={getColumnStyle(index)} {...cell.getCellProps()}>
              {cell.render('Cell')}
            </td>
          )}
        </Fragment>
      );
    });

  // all the data rows
  const bodySection = rows.map((row, index) => {
    prepareRow(row);
    if (row.original.isDraggableRow) {
      return (
        <DraggableRow
          index={index}
          rowClass={rowClass}
          rowId={row.id}
          onMove={moveRow}
          onDrop={rowDropCallback}
          data-test-id={draggableRowTestId}
          {...row.getRowProps(userRowProp ? userRowProp(row) : undefined)}
        >
          {getCells(row)}
        </DraggableRow>
      );
    }
    return (
      <tr
        className={rowClass}
        {...row.getRowProps(userRowProp ? userRowProp(row) : undefined)}
        data-test-id={customRowIdCallback ? row.id : testIdRow}
      >
        {getCells(row)}
      </tr>
    );
  });

  return (
    <>
      {isDraggable ? (
        <DndProvider backend={HTML5Backend}>
          <table className={tableClass} {...getTableProps()} data-test-id={testId}>
            {colSection}
            <thead>{headerSection}</thead>
            <tbody {...getTableBodyProps()}>{bodySection}</tbody>
            <tfoot>{footerSection}</tfoot>
          </table>
        </DndProvider>
      ) : (
        <table className={tableClass} {...getTableProps()} data-test-id={testId}>
          {colSection}
          <thead>{headerSection}</thead>
          <tbody {...getTableBodyProps()}>{bodySection}</tbody>
          <tfoot>{footerSection}</tfoot>
        </table>
      )}
    </>
  );
};

export default Table;
